import React, { useEffect, useState } from 'react';
import API from '../api';
import SEO from "../components/SEO";

const JobsPage = () => {
    const [universityJobs, setUniversityJobs] = useState([]);
    const [alumniJobs, setAlumniJobs] = useState([]);
    const [filter, setFilter] = useState('all'); // all | university | alumni
    const [query, setQuery] = useState('');
    const [loading, setLoading] = useState(true);
    const [selectedJob, setSelectedJob] = useState(null);

    // Ensure the page scrolls to top whenever this page is navigated to or the id changes
    useEffect(() => {
        try {
            if (typeof window !== 'undefined' && window?.scrollTo) {
                // instant jump to top to avoid flashing at previous scroll position
                window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
            }
        } catch (e) {
            // fallback: simple assignment
            try {
                window.scrollTo(0, 0);
            } catch (err) {
                // ignore in environments without window
            }
        }
    }, []);

    useEffect(() => {
        let mounted = true;
        const fetchJobs = async () => {
            setLoading(true);
            try {
                if (API && API.get) {
                    try {
                        const res = await API.get('/jobs');
                        const allJobs = res.data?.data || res.data || [];
                        
                        // Filter active jobs and separate by source
                        const activeJobs = allJobs.filter(job => job.status === 'active');
                        const universityJobsList = activeJobs.filter(job => job.source === 'internal');
                        const alumniJobsList = activeJobs.filter(job => job.source === 'alumni');
                        
                        if (mounted) {
                            setUniversityJobs(universityJobsList);
                            setAlumniJobs(alumniJobsList);
                        }
                    } catch (e) {
                        console.error('Failed to fetch jobs', e);
                        if (mounted) {
                            setUniversityJobs([]);
                            setAlumniJobs([]);
                        }
                    }
                } else {
                    if (mounted) {
                        setUniversityJobs([]);
                        setAlumniJobs([]);
                    }
                }
            } catch (err) {
                console.error('Error fetching jobs', err);
                if (mounted) {
                    setUniversityJobs([]);
                    setAlumniJobs([]);
                }
            } finally {
                if (mounted) setLoading(false);
            }
        };

        fetchJobs();
        return () => (mounted = false);
    }, []);

    const allJobs = [...universityJobs, ...alumniJobs];
    const filtered = allJobs.filter((job) => {
        if (filter === 'university' && job.source !== 'internal') return false;
        if (filter === 'alumni' && job.source !== 'alumni') return false;
        if (!query) return true;
        const q = query.toLowerCase();
        return (
            (job.position || '').toLowerCase().includes(q) ||
            (job.company || '').toLowerCase().includes(q) ||
            (job.description || '').toLowerCase().includes(q) ||
            (job.location || '').toLowerCase().includes(q)
        );
    });

    const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://africaresearchuniversity.com/jobs';
    const jsonLd = {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        'name': 'Job Listings - Africa Research University',
        'description': 'Current job openings at Africa Research University and posts from alumni.',
        'url': pageUrl
    };

    return (
        <section className="min-h-screen py-16 bg-gray-200">
            <SEO 
                title="Jobs — Africa Research University" 
                description="Explore current job openings at ARU and alumni-posted opportunities." 
                image="/assets/img/arulogo.png" 
                url={pageUrl} 
                jsonLd={jsonLd}
                breadcrumbs={[
                    { name: 'Home', url: 'https://africaresearchuniversity.com' },
                    { name: 'Jobs', url: 'https://africaresearchuniversity.com/jobs' }
                ]}
            />
            <div className="max-w-6xl my-16 mx-auto px-6">
                <h1 className="text-2xl md:text-3xl font-bold mb-2">Job Listings</h1>
                <p className="text-gray-600 mb-6">Opportunities posted by the university and alumni.</p>

                <div className="flex flex-wrap gap-3 items-center mb-6">
                    <div className="flex gap-2">
                        <button onClick={() => setFilter('all')} className={`px-4 py-2 rounded ${filter === 'all' ? 'bg-green-600 text-white' : 'bg-white border'}`}>All</button>
                        <button onClick={() => setFilter('university')} className={`px-4 py-2 rounded ${filter === 'university' ? 'bg-green-600 text-white' : 'bg-white border'}`}>University</button>
                        <button onClick={() => setFilter('alumni')} className={`px-4 py-2 rounded ${filter === 'alumni' ? 'bg-green-600 text-white' : 'bg-white border'}`}>Alumni</button>
                    </div>

                    <div className="flex-1">
                        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search jobs, companies, locations..." className="w-full p-3 border rounded" />
                    </div>
                </div>

                {loading ? (
                    <div className="text-center text-gray-600 py-12">Loading jobs...</div>
                ) : filtered.length === 0 ? (
                    <div className="text-center text-gray-600 py-12 bg-white rounded-lg">No jobs match your search.</div>
                ) : (
                    <div className="bg-white rounded-lg shadow-sm divide-y divide-gray-200">
                        {filtered.map((job) => (
                            <div 
                                key={job.id} 
                                className="px-6 py-4 hover:bg-gray-50 transition-colors cursor-pointer"
                                onClick={() => setSelectedJob(job)}
                            >
                                <div className="flex justify-between items-start gap-4">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-3 mb-1">
                                            <h3 className="text-lg font-semibold text-gray-900 truncate">{job.position}</h3>
                                        </div>
                                        <div className="flex items-center gap-3 text-sm text-gray-600 mb-2">
                                            <span className="font-medium">{job.company}</span>
                                            <span>•</span>
                                            <span>{job.location || 'Location TBD'}</span>
                                            <span>•</span>
                                            <span>{job.type || job.jobType || 'Full-time'}</span>
                                        </div>
                                    </div>
                                    <div className="flex-shrink-0">
                                        <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                                            job.source === 'internal' 
                                                ? 'bg-blue-100 text-blue-700' 
                                                : 'bg-purple-100 text-purple-700'
                                        }`}>
                                            {job.source === 'internal' ? 'University' : 'Alumni'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Job Details Modal */}
                {selectedJob && (
                    <div 
                        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
                        onClick={() => setSelectedJob(null)}
                    >
                        <div 
                            className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
                                <h3 className="text-xl font-semibold text-gray-900">Job Details</h3>
                                <button
                                    onClick={() => setSelectedJob(null)}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                            <div className="p-6 space-y-6">
                                <div>
                                    <div className="flex items-center gap-3 mb-3">
                                        <h4 className="text-3xl font-bold text-gray-900">{selectedJob.position}</h4>
                                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                            selectedJob.source === 'internal' 
                                                ? 'bg-blue-100 text-blue-700' 
                                                : 'bg-purple-100 text-purple-700'
                                        }`}>
                                            {selectedJob.source === 'internal' ? 'University' : 'Alumni'}
                                        </span>
                                    </div>
                                    <p className="text-xl text-gray-600">{selectedJob.company}</p>
                                </div>

                                <div className="grid grid-cols-2 gap-4 py-4 border-y">
                                    <div>
                                        <p className="text-sm font-semibold text-gray-500 mb-1">Location</p>
                                        <p className="text-gray-900">{selectedJob.location || 'Location TBD'}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm font-semibold text-gray-500 mb-1">Job Type</p>
                                        <p className="text-gray-900">{selectedJob.type || selectedJob.jobType || 'Full-time'}</p>
                                    </div>
                                    {selectedJob.deadline && (
                                        <div>
                                            <p className="text-sm font-semibold text-gray-500 mb-1">Application Deadline</p>
                                            <p className="text-gray-900">{new Date(selectedJob.deadline).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                                        </div>
                                    )}
                                    {selectedJob.contactEmail && (
                                        <div>
                                            <p className="text-sm font-semibold text-gray-500 mb-1">Contact</p>
                                            <p className="text-gray-900">{selectedJob.contactEmail}</p>
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <p className="text-sm font-semibold text-gray-500 mb-3">Job Description</p>
                                    <p className="text-gray-900 leading-relaxed whitespace-pre-wrap">
                                        {selectedJob.description}
                                    </p>
                                </div>

                                <div className="flex gap-3 pt-4">
                                    <a
                                        href={selectedJob.applicationLink || selectedJob.applyLink || '#'}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex-1 text-center px-6 py-3 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors shadow-sm"
                                    >
                                        Apply Now
                                    </a>
                                    <button
                                        onClick={() => setSelectedJob(null)}
                                        className="px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
};

export default JobsPage;
